import { useMemo, useState } from "react";
import { Ic } from "./icons";
import { useNav } from "../context/NavContext";
import { buildFolderTree, deleteFolder, type FolderDoc } from "../lib/folders";
import type { FicheDoc } from "../lib/fiches";
import { FOLDER_TINT } from "./CreateFolderDialog";
import { DraggableFicheRow, DroppableFolder } from "./DragDrop";

// A sidebar block that renders the user's folders as a 2-level tree, each
// folder being expandable to show the fiches it contains. Click a fiche
// to navigate to it; right-click (or the ⋯ button) to edit / delete the
// folder itself. No drag-and-drop here — that lands in PR #3.

export const SidebarFolderTree = ({
  folders,
  fiches,
  activeFicheId,
  onEditFolder,
}: {
  folders: FolderDoc[];
  fiches: FicheDoc[];
  activeFicheId: string | null;
  onEditFolder: (folder: FolderDoc) => void;
}) => {
  const { go } = useNav();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const tree = useMemo(() => buildFolderTree(folders), [folders]);

  // Index fiches by folderId once per render — O(fiches * folderIds).
  const fichesByFolder = useMemo(() => {
    const map = new Map<string, FicheDoc[]>();
    for (const f of fiches) {
      if (f.archived) continue;
      for (const fid of f.folderIds) {
        const arr = map.get(fid) ?? [];
        arr.push(f);
        map.set(fid, arr);
      }
    }
    return map;
  }, [fiches]);

  // Recursive count: fiche is counted at a parent folder if it lives in any
  // of that parent's descendants (including itself). Useful for seeing at a
  // glance how much a top-level folder holds without expanding it.
  function countRecursive(folderId: string): number {
    const direct = fichesByFolder.get(folderId) ?? [];
    const children = tree.childrenByParent.get(folderId) ?? [];
    const seen = new Set(direct.map((f) => f.id));
    for (const child of children) {
      for (const fi of fichesByFolder.get(child.id) ?? []) seen.add(fi.id);
    }
    return seen.size;
  }

  function toggle(folderId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }

  if (tree.roots.length === 0) {
    return (
      <div className="px-3 py-2 text-[11.5px] text-ink-500 italic">
        Aucun dossier. Cliquez sur + pour créer votre premier dossier.
      </div>
    );
  }

  return (
    <ul className="space-y-0.5">
      {tree.roots.map((folder) => (
        <FolderNode
          key={folder.id}
          folder={folder}
          depth={0}
          children={tree.childrenByParent.get(folder.id) ?? []}
          fichesByFolder={fichesByFolder}
          isExpanded={expanded.has(folder.id)}
          expandedSet={expanded}
          count={countRecursive(folder.id)}
          onToggle={toggle}
          onOpenFiche={(id) => go("fiche", { ficheId: id })}
          onEdit={onEditFolder}
          activeFicheId={activeFicheId}
        />
      ))}
    </ul>
  );
};

function FolderNode({
  folder,
  depth,
  children,
  fichesByFolder,
  isExpanded,
  expandedSet,
  count,
  onToggle,
  onOpenFiche,
  onEdit,
  activeFicheId,
}: {
  folder: FolderDoc;
  depth: number;
  children: FolderDoc[];
  fichesByFolder: Map<string, FicheDoc[]>;
  isExpanded: boolean;
  expandedSet: Set<string>;
  count: number;
  onToggle: (id: string) => void;
  onOpenFiche: (id: string) => void;
  onEdit: (folder: FolderDoc) => void;
  activeFicheId: string | null;
}) {
  const directFiches = fichesByFolder.get(folder.id) ?? [];
  // A node is empty when it has no direct fiches AND no children. We still
  // render it (the user needs to see their folder) but skip the chevron.
  const isEmpty = directFiches.length === 0 && children.length === 0;
  const [menuOpen, setMenuOpen] = useState(false);

  async function handleDelete() {
    const confirmed = window.confirm(
      `Supprimer « ${folder.name} » ?\n\nLes fiches qu'il contient seront retirées du dossier mais pas supprimées.`,
    );
    if (!confirmed) return;
    try {
      await deleteFolder(folder.id);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : String(err));
    }
    setMenuOpen(false);
  }

  return (
    <li>
      <DroppableFolder folderId={folder.id}>
        {({ setNodeRef, isOver }) => (
      <div
        ref={setNodeRef}
        className={`group flex items-center gap-1 h-8 pr-1 rounded-md transition ${
          isOver
            ? "bg-lime-100 ring-2 ring-lime-300"
            : "hover:bg-ink-100"
        }`}
        style={{ paddingLeft: 8 + depth * 12 }}
      >
        <button
          onClick={() => !isEmpty && onToggle(folder.id)}
          className={`w-4 h-4 grid place-items-center text-ink-400 ${
            isEmpty ? "invisible" : ""
          }`}
          aria-label={isExpanded ? "Replier" : "Déplier"}
        >
          <Ic.Chevron
            size={10}
            className={`transition-transform ${isExpanded ? "rotate-90" : ""}`}
          />
        </button>
        <button
          onClick={() => !isEmpty && onToggle(folder.id)}
          className="flex-1 min-w-0 flex items-center gap-2 text-left"
        >
          <span className={`${FOLDER_TINT[folder.color]} shrink-0`}>
            {folder.icon ? (
              <span className="text-[13px]">{folder.icon}</span>
            ) : (
              <Ic.Folder size={13} />
            )}
          </span>
          <span className="flex-1 min-w-0 truncate text-[12.5px] text-ink-700">
            {folder.name}
          </span>
          {count > 0 && (
            <span className="text-[10.5px] font-mono text-ink-400 tabular">
              {count}
            </span>
          )}
        </button>
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            className="w-5 h-5 rounded grid place-items-center text-ink-400 opacity-0 group-hover:opacity-100 hover:bg-ink-200 transition"
            aria-label="Actions du dossier"
          >
            <Ic.Chevron size={10} className="rotate-90" />
          </button>
          {menuOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setMenuOpen(false)}
              />
              <div className="absolute right-0 top-6 z-50 w-36 bg-paper rounded-lg hairline shadow-pop py-1">
                <button
                  onClick={() => {
                    onEdit(folder);
                    setMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-1.5 text-[12.5px] hover:bg-ink-100"
                >
                  Modifier…
                </button>
                <button
                  onClick={handleDelete}
                  className="w-full text-left px-3 py-1.5 text-[12.5px] text-brick hover:bg-rose-50"
                >
                  Supprimer
                </button>
              </div>
            </>
          )}
        </div>
      </div>
        )}
      </DroppableFolder>

      {isExpanded && (
        <ul className="mt-0.5 space-y-0.5">
          {/* Direct fiches first, children folders below. */}
          {directFiches.map((fiche) => (
            <li key={fiche.id}>
              <DraggableFicheRow ficheId={fiche.id}>
                {({ ref, listeners, attributes, style }) => (
                  <button
                    ref={ref as (el: HTMLButtonElement | null) => void}
                    style={{
                      ...style,
                      paddingLeft: 28 + depth * 12,
                    }}
                    {...attributes}
                    {...listeners}
                    onClick={() => onOpenFiche(fiche.id)}
                    className={`w-full flex items-center gap-2 h-7 pr-2 rounded-md text-left text-[12px] truncate transition ${
                      activeFicheId === fiche.id
                        ? "bg-lime-200 text-ink-900"
                        : "text-ink-600 hover:bg-ink-100"
                    }`}
                    title={fiche.title}
                  >
                    <Ic.File size={11} className="text-ink-400 shrink-0" />
                    <span className="truncate">{fiche.title}</span>
                  </button>
                )}
              </DraggableFicheRow>
            </li>
          ))}
          {children.map((child) => (
            <FolderNode
              key={child.id}
              folder={child}
              depth={depth + 1}
              children={[]}
              fichesByFolder={fichesByFolder}
              isExpanded={expandedSet.has(child.id)}
              expandedSet={expandedSet}
              count={(fichesByFolder.get(child.id) ?? []).length}
              onToggle={onToggle}
              onOpenFiche={onOpenFiche}
              onEdit={onEdit}
              activeFicheId={activeFicheId}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
